import { DateTime } from "luxon";

import { showConflictModelPopup } from "@manager/workspacemodels/ModelConflictScreen";
import {
  AccessDeniedError,
  ModeldataNotFoundError,
  NotFoundError
} from "@core/errors/Errors";
import {
  cloudMediaAPI,
  localMediaAPI,
  useMediaStore
} from "@core/media/media.store";

import { extensionFromMime } from "@/helpers/utility/ReadFile";
import { getUserDB } from "@/api/DB";
import { dateOrNow } from "@/helpers/utility/DateTimeFunctions";
import * as API from "@/api/API";
import Compressor from "@/helpers/data/Compressor";
import envOptions from "@/helpers/Env";
import eventHub from "@/helpers/utility/Events";

import { useUserContext } from "@/api/user.store";
import { useWorkspaceModelsDB } from "./models.db";
import { modelLocalAPI } from "./modelAPI";

import type { LocalModelInfo } from "@/api/DB";
import type { Model } from "./Model";
import type {
  LocalModelVersion,
  ModelVersion,
  ModelVersionMeta,
  ModelVersionWithData
} from "./ModelVersion";

export const modelDataCloudAPI = {
  /**
   * Retrieve modelData from the online database
   */
  async get(
    workspaceId: number,
    modelId: number | string,
    requestOptions: API.RequestOptions = {},
    omitMedia: boolean = false
  ) {
    // Get the raw model version data file
    const modelDataPromise = API.get<string>(
      `/v1/workspaces/${workspaceId}/models/${modelId}/versions/last`,
      {
        ...requestOptions,
        responseType: "text",
        parseResponse(response) {
          return Compressor.decompress(response.text);
        }
      } as API.RequestOptions<string>
    );

    // Get the version meta data (id, created, media...)
    const modelDataMetaPromise = API.get<ModelVersionMeta>(
      `/v1/workspaces/${workspaceId}/models/${modelId}/versions/last/meta`,
      {
        ...requestOptions
      } as API.RequestOptions<ModelVersionMeta>
    );

    // Wait until both requests have arrived
    const [modelData, modelMetaData] = await Promise.all([
      modelDataPromise,
      modelDataMetaPromise
    ]);

    // load all media files
    const mediaEntries = omitMedia ? [] : await cloudMediaAPI.loadModelMedia(
      modelMetaData.media,
      requestOptions
    );

    return {
      data: modelData,
      created: modelMetaData.created,
      id: modelMetaData.id,
      appVersion: modelMetaData.appVersion,
      model: typeof modelId == "number" ? modelId : parseInt(modelId),
      media: mediaEntries
    } as ModelVersionWithData;
  },

  /**
   * Save a new version of the modelData to the cloud database
   * @param options.modelData encrypted model data
   * @param options.newMediaUUIDs data of the new media entries
   * @param options.media all media entries
   */
  async update(
    workspaceId: number,
    modelId: number | string,
    options: {
      modelData: string;
      fileName: string;
      lastVersionId?: string | number | null;
      appVersion?: string | null;
      newMediaUUIDs: string[];
      media: { uuid: string; data: Blob }[];
    },
    requestOptions: API.RequestOptions<ModelVersionMeta> = {}
  ) {
    const {
      modelData,
      fileName,
      lastVersionId,
      media = [],
      newMediaUUIDs = [],
      appVersion = null
    } = options;

    const blob = Compressor.compressToBlob(modelData);
    const mediaAttachments = buildMediaAttachments(media, newMediaUUIDs);
    const allMediaUUIDs = media.map((entry) => entry.uuid);

    const fileAttachment = {
      fileName,
      type: "simcisionModel",
      data: blob
    };

    requestOptions = {
      ...requestOptions,
      data: {
        media: allMediaUUIDs,
        lastVersionId,
        appVersion
      },
      attach: {
        ...mediaAttachments,
        file: fileAttachment
      }
    };

    const dataMeta = await API.post<ModelVersionMeta>(
      `/v1/workspaces/${workspaceId}/models/${modelId}/versions`,
      requestOptions
    ).catch((error) => {
      // If the server can't find any media references,
      // RETRY with the missing media files.
      if (error.code == "missing_media") {
        const missingMediaUUIDs = error.fields.missing_media;
        console.warn("retry with missing media", missingMediaUUIDs);
        const missingMediaAttachments = buildMediaAttachments(
          media,
          missingMediaUUIDs
        );

        // send the request again, with the missing media
        requestOptions.attach = {
          ...missingMediaAttachments,
          ...mediaAttachments,
          file: fileAttachment
        };
        return API.post<ModelVersionMeta>(
          `/v1/workspaces/${workspaceId}/models/${modelId}/versions`,
          requestOptions
        );
      }

      console.error(error);
      throw error;
    });

    return {
      data: modelData,
      created: dataMeta.created,
      id: dataMeta.id, // id of the modelData entry
      model: typeof modelId == "number" ? modelId : parseInt(modelId), // id of the model
      media: dataMeta.media,
      appVersion: dataMeta.appVersion
    } as ModelVersion & { media: { uuid: string; data: Blob }[] };
  }
};

/**
 * Create an attachment list that can be sent as file attachments
 * to the server via multipart/formdata.
 */
function buildMediaAttachments(
  mediaList: { uuid: string; data: Blob }[],
  uuids: string[]
) {
  const mediaAttachments: Record<
    string,
    { uuid: string; data: Blob; fileName: string; type: "media" }
  > = {};

  for (const mediaEntry of mediaList) {
    const mediaUuid = mediaEntry.uuid;
    if (!uuids.includes(mediaUuid)) continue;

    const data = mediaEntry.data;

    const extension = extensionFromMime(data?.type);

    mediaAttachments[`image-${mediaUuid}`] = {
      uuid: mediaUuid,
      fileName: mediaUuid + extension,
      type: "media",
      data: data
    };
  }

  return mediaAttachments;
}

export const modelDataLocalAPI = {
  async get(workspaceId: number, modelId: number | string, requestOptions = {}, omitMedia: boolean = false) {
    const modelVersionsDB = getModelVersionsDB();
    modelId = toIntIfNotUuid(modelId);
    const versionEntry = await modelVersionsDB.get({
      model: modelId
    });

    if (!versionEntry) {
      return null;
    }

    // load and attach media files
    const mediaEntries = omitMedia ? [] : await localMediaAPI.loadModelMedia(
      versionEntry?.media ?? [],
      modelId
    );
    versionEntry.media = mediaEntries;

    return versionEntry as ModelVersionWithData & LocalModelVersion;
  },

  /**
   * Save the modelData to the local db.
   * @param options.modelData encrypted model data
   * @param options.newMediaUUIDs data of the new media entries
   * @param options.media all connected media uuids
   */
  async update(
    workspaceId: number,
    modelId: string | number,
    options: {
      modelData: string;
      fileName: string;
      lastVersionId?: string | null | number;
      media: { uuid: string; data: Blob }[];
      appVersion?: string | null;
    }
  ) {
    const { modelData, lastVersionId, media, appVersion = null } = options;

    const _modelId = toIntIfNotUuid(modelId);

    // 1. Step:
    // Save the model in the local database:
    const { getModelsDB } = useWorkspaceModelsDB();
    const modelsDB = getModelsDB();
    const modelVersionsDB = getModelVersionsDB();

    const dbModelInfo = await modelsDB.get({ id: _modelId });

    if (dbModelInfo == null) {
      throw new NotFoundError();
    }

    // If the local state is locked, do not save it.
    if (dbModelInfo.isLocked) {
      throw new AccessDeniedError();
    }

    await localMediaAPI.saveForModel(media, { model: _modelId });

    const lastVersion = await modelVersionsDB.get({ model: _modelId });

    const data: LocalModelVersion = {
      model: _modelId,
      created: new Date().toISOString(),
      id: lastVersionId ?? lastVersion?.id,
      lastSynch: lastVersion?.lastSynch ?? null,
      data: modelData,
      media: media.map((mediaEntry) => ({ uuid: mediaEntry.uuid })),
      appVersion: appVersion
    };

    console.debug("localDB: save modelData", data);
    await modelVersionsDB.put({ ...data }, _modelId);

    // model is now saved in local database

    // retrieve combined data version:
    const modelWithData = await modelDataLocalAPI.get(workspaceId, modelId);

    if (modelWithData == null) {
      throw new ModeldataNotFoundError();
    }

    eventHub.emit("db-modeldata-updated", _modelId, modelWithData);

    return modelWithData;
  },

  // ==== SYNCHRONIZATION ====
  // ↓

  /**
   * Transfers a model from the online database to the local database
   */
  async download(workspaceId: number, modelId: string | number) {
    try {
      const _modelId = toIntIfNotUuid(modelId);

      const modelData = await modelDataCloudAPI.get(workspaceId, _modelId);

      const mediaEntries = modelData?.media ?? [];

      // Save the version to the local database
      const modelVersionsDB = getModelVersionsDB();
      await modelVersionsDB.put({
        ...modelData,
        media: mediaEntries.map((entry) => ({ uuid: entry.uuid })),
        lastSynch: dateOrNow(modelData.created)
      });
      await localMediaAPI.saveForModel(mediaEntries, {
        model: _modelId,
        uploaded: true
      });

      const savedVersion = await modelDataLocalAPI.get(workspaceId, _modelId);
      eventHub.emit("db-modeldata-updated", _modelId, savedVersion);
      return savedVersion;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  /**
   * UPLOAD a modeldata to the online database from the local database
   */
  async upload(localId: string | number) {
    const modelId = toIntIfNotUuid(localId);

    // Get the local model entry from the local DB:
    const modelsDB = useWorkspaceModelsDB().getModelsDB();
    const localModel = await modelsDB.get({ id: modelId });

    if (localModel == null) {
      throw new NotFoundError();
    }

    // Get the local data version
    const modelVersionsDB = getModelVersionsDB();
    const localModelData = await modelVersionsDB.get({ model: modelId });
    if (localModelData == null) {
      throw new NotFoundError();
    }

    const { data, media = [], id: lastVersionId } = localModelData;

    if (data == null) {
      throw new Error(`Local model ${lastVersionId} has no data yet.`);
    }

    // Get all media entries that have not been uploaded yet
    const localMediaEntries = await localMediaAPI.loadModelMedia(
      media,
      modelId
    );

    const newMediaUUIDs = localMediaEntries
      .filter((entry) => !entry.uploaded)
      .map((entry) => entry.uuid);

    // set the filename for the download file
    const modelName = localModel.name;
    const fileName = `${modelName}.${
      localModel.isSimulationGame ? "sim" : "model"
    }`;

    const workspaceId = localModel.workspace;

    const uploadedModelData = await modelDataCloudAPI.update(
      workspaceId,
      modelId,
      {
        modelData: data,
        fileName,
        lastVersionId: localModel?.lastVersion?.id ?? lastVersionId,
        media: localMediaEntries,
        newMediaUUIDs: newMediaUUIDs,
        appVersion: localModelData.appVersion ?? null
      }
    );

    const newMedia = uploadedModelData?.media ?? [];

    await modelVersionsDB.put({
      ...uploadedModelData,
      media: newMedia.map((entry) => ({ uuid: entry.uuid })),
      lastSynch: dateOrNow(uploadedModelData.created)
    });

    // mark all images as uploaded:
    const mediaStore = useMediaStore();
    const mediaUUIDs = media.map((entry) => entry.uuid);
    await mediaStore.markMediaAsUploaded(mediaUUIDs);

    const savedModelData = await modelDataLocalAPI.get(workspaceId, modelId);

    if (savedModelData == null) {
      throw new ModeldataNotFoundError();
    }

    // save the last version info to the models store,
    // this makes sure synchronization works.
    const updatedModel = await modelsDB.updateAndGet(modelId, {
      lastVersion: {
        id: savedModelData.id,
        created: savedModelData.created,
        model: modelId,
        lastSynch: savedModelData.lastSynch
      },

      // ↓↓ important!
      //      Leave this line here, otherwise new 'modified' date will be set.
      //      The modified date is used for synchronization.
      modified: localModel.modified
    });
    eventHub.emit("db-model-updated", modelId, updatedModel);

    eventHub.emit("db-modeldata-updated", modelId, savedModelData);

    console.debug("localDB: modelData uploaded.", savedModelData);

    return savedModelData;
  },

  /**
   * Transfer modeldata from one model to another.
   * @param {(number|string)} currentModelId
   * @param {(number|string)} newModelId
   * @returns was anything transferred?
   */
  async transfer(currentModelId: string | number, newModelId: string | number) {
    const currentId = toIntIfNotUuid(currentModelId);
    const targetId = newModelId;

    // load the current modeldata from the db
    const modelVersionsDB = getModelVersionsDB();
    const modelData = await modelVersionsDB.get({ model: currentId });
    if (!modelData) {
      return false;
    }

    await modelVersionsDB.update(currentId, { model: targetId });
    await localMediaAPI.replaceModelId(currentId, targetId);

    return true;
  },

  /**
   * Copy modeldata from one model to another
   */
  async copy(currentModelId: number | string, newModelId: number | string) {
    const currentId = toIntIfNotUuid(currentModelId);
    const targetId = newModelId;

    // load the current modeldata from the db
    const modelVersionsDB = getModelVersionsDB();
    const modelData = await modelVersionsDB.get({ model: currentId });
    if (!modelData) {
      throw new NotFoundError();
    }

    // change the main attributes
    modelData.model = targetId;
    modelData.lastSynch = null;
    modelData.created = new Date().toISOString();

    // create the new modeldata
    const newKey = await modelVersionsDB.put(modelData);
    const newModelData = await modelVersionsDB.get(newKey);

    if (!newModelData) {
      throw new Error("Error while creating new model data");
    }

    await localMediaAPI.copyModelMedia(currentId, targetId);

    eventHub.emit("db-modeldata-created", targetId, newModelData);
    return newModelData;
  },

  /**
   * Removes the modeldata of a model in the database.
   */
  async delete(modelId: string | number) {
    modelId = toIntIfNotUuid(modelId);
    await getModelVersionsDB().delete(modelId);

    await localMediaAPI.removeMediaOfModel(modelId);
    return true;
  },

  /**
   * @param localModel model info of a recently synched model
   */
  async synchModelData(localModel: LocalModelInfo) {
    const modelId = toIntIfNotUuid(localModel.id);

    const modelVersionsDB = getModelVersionsDB();
    const lastModelVersion = localModel.lastVersion;
    const localModelVersion = await modelVersionsDB.get(modelId);

    if (lastModelVersion == null && localModelVersion == null) {
      // No data? Don't do anything.
      console.log(
        `%cmodel ${localModel.id} ${localModel.name} has no data anywhere`,
        "color: grey;"
      );
      return;
    }

    if (lastModelVersion == null && localModelVersion != null) {
      console.log("No model data online, uploading", modelId, localModel.name);
      return modelDataLocalAPI.upload(modelId);
    } else if (localModelVersion == null && lastModelVersion != null) {
      console.log(
        "No model data offline, downloading",
        modelId,
        localModel.name
      );
      return modelDataLocalAPI.download(localModel.workspace, modelId);
    }

    const localDataCreated = DateTime.fromJSDate(
      localModelVersion?.created
        ? new Date(localModelVersion.created)
        : new Date(0)
    );
    const cloudDataCreated = DateTime.fromJSDate(
      lastModelVersion?.created
        ? new Date(lastModelVersion.created)
        : new Date(0)
    );

    const lastSynch = localModelVersion?.lastSynch
      ? DateTime.fromJSDate(new Date(localModelVersion?.lastSynch))
      : DateTime.min(localDataCreated, cloudDataCreated);

    const localDataChanged = localDataCreated > lastSynch;
    const cloudDataChanged = cloudDataCreated > lastSynch;

    if (!localDataChanged && !cloudDataChanged) {
      return;
    }

    if (localDataChanged && !cloudDataChanged) {
      console.log(`upload ${localModel.id} (data changed)`);
      return modelDataLocalAPI.upload(localModel.id);
    } else if (cloudDataChanged && !localDataChanged) {
      // DOWNLOAD modelData
      console.log(`download ${localModel.id} (data changed)`);
      return modelDataLocalAPI.download(localModel.workspace, localModel.id);
    }

    if (localDataChanged && cloudDataChanged) {
      // CONFLICT!

      console.log(`DATA CONFLICT ${localModel.id}`);

      const modelsDB = useWorkspaceModelsDB().getModelsDB();

      const localModelEntry = await modelsDB.get({ id: localModel.id });
      if (!localModelEntry) {
        throw new Error("local model entry does not exist");
      }

      const remoteModel = await API.get<Model>(
        `/v1/workspaces/${localModelEntry.workspace}/models/${localModelEntry.id}`
      );

      localModelEntry.lastVersion = localModelVersion;

      const chosenAction = await showConflictModelPopup(
        localModelEntry,
        remoteModel,
        "data"
      );

      switch (chosenAction) {
        case "remote":
          return modelDataLocalAPI.download(
            localModelEntry.workspace,
            localModelEntry.id
          );

        case "local":
          return modelDataLocalAPI.upload(localModelEntry.id);

        case "both":
          await modelLocalAPI.resolveConflictWithBoth(
            localModelEntry,
            remoteModel
          );
          return modelDataLocalAPI.download(remoteModel.workspace, modelId);

        default:
          console.warn("option not defined");
          return;
      }
    }
  }
};

export const modelDataAPI = envOptions.saveLocally
  ? modelDataLocalAPI
  : modelDataCloudAPI;

/**
 * Returns the local db workspace table
 */
function getModelVersionsDB() {
  const { user } = useUserContext();
  const db = getUserDB(user?.id);
  return db.modelVersions;
}

/**
 * Makes sure not to transform uuids
 * @returns The number as int or the uuid
 */
function toIntIfNotUuid(str: string | number) {
  if (typeof str != "string") return str;
  if (str.includes("-")) {
    return str;
  }
  return parseInt(str);
}
